/**
 * Carousel Manager Module
 * Universal carousel management for all carousels sitewide
 * Handles Flickity, Slick, and custom carousels with stabilization and trackpad support
 */

// Wait for CarouselCore to be available
(function() {
    // Define HomepageCarouselManager globally first
    const HomepageCarouselManager = {
      // Performance monitoring
      initCount: 0,
      maxInitTime: 2000,

      // Shared utilities
      utils: {
        // Debounced resize handler - only for homepage carousels
        resizeTimer: null,
        resizeHandlers: new Map(),

        // Performance-optimized DOM measurements with RAF batching
        measureCache: new Map(),
        lastMeasureTime: 0,
        measureThrottle: 50, // 20fps max instead of 60fps
        pendingMeasurements: new Set(),
        rafId: null,

        // Batch DOM measurements to prevent layout thrashing
        batchMeasurements() {
          if (this.rafId) return;

          this.rafId = requestAnimationFrame(() => {
            const startTime = performance.now();
            const maxFrameTime = 8; // Stay well under 16.67ms budget

            // Process measurements in chunks to avoid blocking the frame
            const measurements = Array.from(this.pendingMeasurements);
            this.pendingMeasurements.clear();

            let processed = 0;
            const processChunk = () => {
              const chunkStart = performance.now();

              // Process up to 5 measurements per chunk, or until we hit time limit
              while (processed < measurements.length && (performance.now() - chunkStart) < maxFrameTime) {
                try {
                  measurements[processed]();
                } catch(e) {
                  console.warn('Measurement error:', e);
                }
                processed++;
              }

              // If we have more measurements, schedule another frame
              if (processed < measurements.length) {
                this.rafId = requestAnimationFrame(processChunk);
              } else {
                this.rafId = null;
              }
            };

            processChunk();
          });
        },

        // Queue measurement for batching
        queueMeasurement(callback) {
          this.pendingMeasurements.add(callback);
          this.batchMeasurements();
        },

        // Hardware acceleration helpers with CSS containment
        enableHardwareAcceleration(element) {
          element.style.transform = 'translateZ(0)';
          element.style.willChange = 'transform';
          element.style.contain = 'layout style paint';
        },

        // Anti-flickering for product card elements with optimized selectors
        stabilizeProductElements(container) {
          // Comprehensive selector for all badge and quick-add elements
          const elements = container.querySelectorAll(`
            .product-card__new-tag,
            .product-card__logo-badge-container,
            .product-card__soldout-tag,
            .product-card__quickadd-desktop-btn,
            .product-card__quickadd-mobile-btn,
            .product-card__quickadd-desktop-btn--open,
            .product-card__quickadd-mobile-btn--open,
            .badge__logo-wrapper,
            .badge__messaging-wrapper,
            .coll_prd_comming_soon_notify_3,
            .coll_prd_comming_soon_notify_4,
            .coll_prd_comming_soon__notify_meta
          `);

          // Skip if no elements found
          if (elements.length === 0) {
            return;
          }

          // Use requestAnimationFrame for smooth updates
          requestAnimationFrame(() => {
            // Batch style updates to prevent reflows
            const updates = [];
            elements.forEach(el => {
              // Only update if not already stabilized
              if (!el.dataset.stabilized) {
                updates.push({
                  element: el,
                  styles: {
                    transform: 'translateZ(0)',
                    webkitTransform: 'translateZ(0)',
                    webkitBackfaceVisibility: 'hidden',
                    backfaceVisibility: 'hidden',
                    contain: 'layout',
                    willChange: 'auto' // Use 'auto' instead of 'transform' to prevent layer conflicts
                  }
                });
                el.dataset.stabilized = 'true';
              }
            });

            // Apply all updates in a single batch
            updates.forEach(update => {
              Object.assign(update.element.style, update.styles);
            });
          });
        },

        // Optimized image loading with intersection observer
        setupLazyImageLoading(container) {
          if (!('IntersectionObserver' in window)) return;

          const imageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                const img = entry.target;
                if (img.dataset.src) {
                  img.src = img.dataset.src;
                  img.removeAttribute('data-src');
                  imageObserver.unobserve(img);
                }
              }
            });
          }, { rootMargin: '50px 0px' });

          container.querySelectorAll('img[data-src]').forEach(img => {
            imageObserver.observe(img);
          });
        }
      },

      // Logging with performance metrics
      logInit(name, startTime) {
        this.initCount++;
        const duration = performance.now() - startTime;
      },

      // Unified resize handling - only for homepage carousels
      addResizeHandler(carouselId, handler) {
        this.utils.resizeHandlers.set(carouselId, handler);
      },

      removeResizeHandler(carouselId) {
        this.utils.resizeHandlers.delete(carouselId);
      },

      triggerResize() {
        clearTimeout(this.utils.resizeTimer);
        this.utils.resizeTimer = setTimeout(() => {
          this.utils.resizeHandlers.forEach(handler => {
            try { handler(); } catch(e) { console.warn('Homepage carousel resize error:', e); }
          });
        }, 300); // Slightly higher than global to reduce conflicts
      }
    };

    function initHomepageCarousels() {
      if (typeof window.CarouselCore === 'undefined') {
        setTimeout(initHomepageCarousels, 50);
        return;
      }

      // Initialize HomepageCarouselManager resize handling
      window.addEventListener('resize', () => {
        HomepageCarouselManager.triggerResize();
      });
      window.addEventListener('orientationchange', () => {
        HomepageCarouselManager.triggerResize();
      });

      // Override the s-carousel _onReady method to add stabilization
      const originalCarouselReady = window.Carousel?.prototype?._onReady;
      if (originalCarouselReady) {
        window.Carousel.prototype._onReady = function() {
          // Call original method
          originalCarouselReady.call(this);

          // Apply stabilization for ALL carousels with product cards
          if (this.classList.contains('cs-collection-carousel') ||
              this.querySelector('.product-card')) {
            requestAnimationFrame(() => {
              HomepageCarouselManager.utils.stabilizeProductElements(this);
            });
          }
        };
      }

      // Also hook into Flickity initialization directly
      if (window.Flickity) {
        const originalFlickity = window.Flickity;
        window.Flickity = function(element, options) {
          const instance = new originalFlickity(element, options);

          // Apply stabilization for ALL carousels with product cards
          if (element.classList.contains('cs-collection-carousel') ||
              element.querySelector('.product-card')) {
            requestAnimationFrame(() => {
              HomepageCarouselManager.utils.stabilizeProductElements(element);
            });
          }

          return instance;
        };

        // Copy static properties
        Object.setPrototypeOf(window.Flickity, originalFlickity);
        Object.assign(window.Flickity, originalFlickity);
      }

      // Multiple fallbacks to catch all timing scenarios
      const runStabilization = () => {
        // Target ALL carousels with product cards sitewide
        document.querySelectorAll('s-carousel, .slick-initialized').forEach(carousel => {
          // Only stabilize if carousel contains product cards
          if (carousel.querySelector('.product-card')) {
            HomepageCarouselManager.utils.stabilizeProductElements(carousel);
          }
        });
      };

      // Run stabilization at multiple intervals to catch all timing scenarios
      setTimeout(runStabilization, 500);   // Early fallback
      setTimeout(runStabilization, 1000);  // Medium fallback
      setTimeout(runStabilization, 2000);  // Late fallback
      setTimeout(runStabilization, 3000);  // Final fallback

      // Sitewide carousel detection and stabilization
      (function() {
        let isInitialized = false;

        function initializeSitewideStabilization() {
          if (isInitialized) return;
          isInitialized = true;


          // Enhanced MutationObserver for ALL carousels
          if ('MutationObserver' in window) {
            const observer = new MutationObserver((mutations) => {
              let shouldStabilize = false;

              mutations.forEach(mutation => {
                if (mutation.type === 'childList') {
                  mutation.addedNodes.forEach(node => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                      // Check if added node is a carousel or contains carousels
                      if (node.matches && (
                          node.matches('s-carousel') ||
                          node.matches('.slick-initialized') ||
                          node.matches('.flickity-enabled') ||
                          node.querySelector('s-carousel') ||
                          node.querySelector('.slick-initialized') ||
                          node.querySelector('.flickity-enabled')
                        )) {
                        shouldStabilize = true;
                      }
                    }
                  });
                }
              });

              if (shouldStabilize) {
                requestAnimationFrame(() => {
                  runStabilization();
                });
              }
            });

            // Start observing the entire document
            observer.observe(document.body, {
              childList: true,
              subtree: true
            });
          }

          // Enhanced scroll event handler for ALL carousels
          const handleScroll = () => {
            // Find all visible carousels with product cards
            document.querySelectorAll('s-carousel, .slick-initialized, .flickity-enabled').forEach(carousel => {
              if (carousel.querySelector('.product-card')) {
                HomepageCarouselManager.utils.stabilizeProductElements(carousel);
              }
            });
          };

          // Throttled scroll handler
          let scrollTimeout;
          const throttledScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(handleScroll, 100);
          };

          // Add scroll listeners to all carousels
          document.addEventListener('scroll', throttledScroll, { passive: true });

          // Add Flickity-specific events
          document.addEventListener('flickity:scroll', handleScroll);
          document.addEventListener('flickity:lazyLoad', handleScroll);
          document.addEventListener('flickity:imagesLoaded', handleScroll);

          // Add Slick-specific events
          document.addEventListener('afterChange', handleScroll);
        }

        // Initialize when DOM is ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', initializeSitewideStabilization);
        } else {
          initializeSitewideStabilization();
        }

        // Also initialize after a delay to catch dynamically loaded content
        setTimeout(initializeSitewideStabilization, 1000);
      })();

      // Universal Trackpad Scrolling Support
      (function() {
        const THRESHOLD = 60; // horizontal pixels per slide
        const acc = new WeakMap(); // accumulated deltaX
        const lock = new WeakMap(); // animation lock

        function getCarouselAPI(viewport) {
          // Check for Flickity carousel
          if (viewport.classList.contains('flickity-viewport') && window.Flickity) {
            const flickityInstance = window.Flickity.data(viewport.parentNode);
            return { type: 'flickity', instance: flickityInstance };
          }

          // Check for Slick carousel
          if (viewport.classList.contains('slick-list') && window.jQuery && $.fn.slick) {
            const $slick = $(viewport).closest('.slick-initialized');
            if ($slick.length) {
              return { type: 'slick', instance: $slick };
            }
          }

          return null;
        }

        function onWheelCapture(e) {
          const target = e.target;
          const viewport = target && target.closest(`
            .flickity-enabled .flickity-viewport,
            .slick-initialized .slick-list,
            .cs-collection-carousel,
            .product-collection__carousel
          `);

          if (!viewport) return;

          const dx = e.deltaX || 0;
          const dy = e.deltaY || 0;
          const ax = Math.abs(dx);
          const ay = Math.abs(dy);

          // Determine if this is a horizontal scroll
          const isHorizontal = (ax > ay && ax > 2) || (e.shiftKey && ay > 2);
          if (!isHorizontal) return;

          // Prevent default scrolling behavior
          e.preventDefault();
          e.stopPropagation();

          // Check if carousel is locked (animating)
          if (lock.get(viewport)) return;

          // Accumulate delta for smooth scrolling
          const sum = (acc.get(viewport) || 0) + dx;
          if (Math.abs(sum) < THRESHOLD) {
            acc.set(viewport, sum);
            return;
          }

          // Reset accumulator and lock carousel
          acc.set(viewport, 0);
          lock.set(viewport, true);

          const dirNext = sum > 0; // right swipe -> next
          const api = getCarouselAPI(viewport);

          if (api) {
            if (api.type === 'flickity') {
              // Flickity carousel
              api.instance.uiChange();
              dirNext ? api.instance.next() : api.instance.previous();

              // Unlock after animation completes
              const unlock = () => {
                lock.set(viewport, false);
                api.instance.off('settle', unlock);
              };
              api.instance.on('settle', unlock);

            } else if (api.type === 'slick') {
              // Slick carousel
              api.instance.slick(dirNext ? 'slickNext' : 'slickPrev');

              // Unlock after animation completes
              api.instance.one('afterChange', () => {
                lock.set(viewport, false);
              });
            }
          } else {
            // Fallback: native horizontal scrolling
            viewport.scrollLeft += dirNext ? THRESHOLD : -THRESHOLD;
            lock.set(viewport, false);
          }
        }

        // Bind wheel event with capture phase for priority
        document.addEventListener('wheel', onWheelCapture, { passive: false, capture: true });
      })();

      // Collection Tabs Carousel integration with optimized anti-flickering
      // Skip collection-tabs-carousel as it now uses CSS-only carousel
      const collectionTabsCarousels = document.querySelectorAll('.collection-tabs-carousel s-carousel:not(.css-collection-carousel)');
      collectionTabsCarousels.forEach(carousel => {
        let scrollTimeout = null;
        let isStabilized = false;
        let mutationObserver = null;

        // Function to stabilize elements and reset flag
        const stabilizeElements = () => {
          HomepageCarouselManager.utils.stabilizeProductElements(carousel);
          isStabilized = true;
        };

        // Apply initial stabilization
        stabilizeElements();

        // Set up MutationObserver to watch for dynamically added product cards
        if ('MutationObserver' in window) {
          mutationObserver = new MutationObserver((mutations) => {
            let shouldStabilize = false;

            mutations.forEach(mutation => {
              if (mutation.type === 'childList') {
                // Check if any added nodes contain product cards
                mutation.addedNodes.forEach(node => {
                  if (node.nodeType === Node.ELEMENT_NODE) {
                    if (node.classList && node.classList.contains('collection-carousel__slide')) {
                      shouldStabilize = true;
                    } else if (node.querySelector && node.querySelector('.collection-carousel__slide')) {
                      shouldStabilize = true;
                    }
                  }
                });
              }
            });

            // Stabilize if new product cards were added
            if (shouldStabilize) {
              requestAnimationFrame(() => {
                stabilizeElements();
              });
            }
          });

          // Start observing the carousel for changes
          mutationObserver.observe(carousel, {
            childList: true,
            subtree: true
          });
        }

        // Optimized scroll handler with debouncing
        const handleScroll = () => {
          // Clear existing timeout
          if (scrollTimeout) {
            clearTimeout(scrollTimeout);
          }

          // Only re-stabilize if not already done and after scroll ends
          scrollTimeout = setTimeout(() => {
            if (!isStabilized) {
              stabilizeElements();
            }
          }, 100);
        };

        const handleChange = () => {
          isStabilized = false;
        };

        carousel.addEventListener('flickity:scroll', handleScroll);
        carousel.addEventListener('flickity:change', handleChange);

        // Store cleanup function for potential future use
        carousel._cleanupCarousel = () => {
          if (scrollTimeout) {
            clearTimeout(scrollTimeout);
          }
          if (mutationObserver) {
            mutationObserver.disconnect();
          }
          carousel.removeEventListener('flickity:scroll', handleScroll);
          carousel.removeEventListener('flickity:change', handleChange);
        };
      });

      // Product Collection Carousel integration with optimized anti-flickering
      const productCollectionCarousels = document.querySelectorAll('.product-collection s-carousel');
      productCollectionCarousels.forEach(carousel => {
        let scrollTimeout = null;
        let isStabilized = false;
        let mutationObserver = null;

        // Function to stabilize elements and reset flag
        const stabilizeElements = () => {
          HomepageCarouselManager.utils.stabilizeProductElements(carousel);
          isStabilized = true;
        };

        // Apply initial stabilization
        stabilizeElements();

        // Set up MutationObserver to watch for dynamically added product cards
        if ('MutationObserver' in window) {
          mutationObserver = new MutationObserver((mutations) => {
            let shouldStabilize = false;

            mutations.forEach(mutation => {
              if (mutation.type === 'childList') {
                // Check if any added nodes contain product cards
                mutation.addedNodes.forEach(node => {
                  if (node.nodeType === Node.ELEMENT_NODE) {
                    if (node.classList && node.classList.contains('product-collection__slide')) {
                      shouldStabilize = true;
                    } else if (node.querySelector && node.querySelector('.product-collection__slide')) {
                      shouldStabilize = true;
                    }
                  }
                });
              }
            });

            // Stabilize if new product cards were added
            if (shouldStabilize) {
              requestAnimationFrame(() => {
                stabilizeElements();
              });
            }
          });

          // Start observing the carousel for changes
          mutationObserver.observe(carousel, {
            childList: true,
            subtree: true
          });
        }

        // Optimized scroll handler with debouncing
        const handleScroll = () => {
          // Clear existing timeout
          if (scrollTimeout) {
            clearTimeout(scrollTimeout);
          }

          // Only re-stabilize if not already done and after scroll ends
          scrollTimeout = setTimeout(() => {
            if (!isStabilized) {
              stabilizeElements();
            }
          }, 100);
        };

        const handleChange = () => {
          isStabilized = false;
        };

        carousel.addEventListener('flickity:scroll', handleScroll);
        carousel.addEventListener('flickity:change', handleChange);

        // Store cleanup function for potential future use
        carousel._cleanupCarousel = () => {
          if (scrollTimeout) {
            clearTimeout(scrollTimeout);
          }
          if (mutationObserver) {
            mutationObserver.disconnect();
          }
          carousel.removeEventListener('flickity:scroll', handleScroll);
          carousel.removeEventListener('flickity:change', handleChange);
        };
      });
    }

    /* ===== OPTIMIZED HERO CAROUSEL ===== */
    (function () {
      function initHero(root) {
        const startTime = performance.now();
        var $root   = jQuery(root);
        var $slider = $root.find('.js-hero-carousel');
        if (!$slider.length || $slider.hasClass('slick-initialized')) return;

        // Get carousel options from data attribute
        var carouselOptions = $slider.data('slick');
        if (!carouselOptions) return;

        // Enable hardware acceleration
        HomepageCarouselManager.utils.enableHardwareAcceleration($slider[0]);

        // Initialize Slick carousel
        $slider.slick(carouselOptions);

        // Log performance
        const endTime = performance.now();
        const initTime = endTime - startTime;
        if (initTime > HomepageCarouselManager.maxInitTime) {
          console.warn(`Hero carousel init took ${initTime.toFixed(2)}ms - consider optimizing`);
        } else {
          console.log(`Hero carousel initialized in ${initTime.toFixed(2)}ms`);
        }

        // Mark as initialized
        HomepageCarouselManager.initCount++;
      }

      function initAllVisible() {
        if (!window.jQuery || !jQuery.fn || !jQuery.fn.slick) return;
        jQuery('.hero-section').each(function () { initHero(this); });
      }

      document.addEventListener('DOMContentLoaded', initAllVisible);

      document.addEventListener('shopify:section:load', function (e) {
        var $sec = jQuery(e.target).find('.hero-section');
        if ($sec.length) initHero($sec[0]);
      });
    })();

    /* ===== OPTIMIZED MINI HERO CAROUSEL ===== */
    (function () {
      function initMiniHero(root) {
        const startTime = performance.now();
        var $root   = jQuery(root);
        var $slider = $root.find('.js-mini-hero-slider');
        if (!$slider.length || $slider.hasClass('slick-initialized')) return;

        // ─── Settings ───────────────────────────────────────────────────────────
        var BASE_GAP     = parseInt($slider.data('gap'), 10) || 20;
        var DESKTOP_BP   = parseInt($slider.data('desktop-breakpoint'), 10) || 1024;
        var SHOW_DESKTOP = parseInt($slider.data('show-desktop'), 10) || 4;
        var SHOW_MOBILE  = parseInt($slider.data('show-mobile'), 10)  || 1;

        function isDesktop () { return window.matchMedia('(min-width:' + DESKTOP_BP + 'px)').matches; }
        function cols()       { return isDesktop() ? SHOW_DESKTOP : SHOW_MOBILE; }

        // Optimized railWidth with batching and caching
        function railWidth()  {
          const now = performance.now();
          if (now - HomepageCarouselManager.utils.lastMeasureTime < HomepageCarouselManager.utils.measureThrottle) {
            return HomepageCarouselManager.utils.measureCache.get('miniHero') || 0;
          }

          const element = $slider[0];
          if (!element) return 0;

          // Use batched measurement to prevent layout thrashing
          let width = HomepageCarouselManager.utils.measureCache.get('miniHero') || 0;

          HomepageCarouselManager.utils.queueMeasurement(() => {
            const rect = element.getBoundingClientRect();
            width = rect ? Math.round(rect.width || 0) : 0;
            HomepageCarouselManager.utils.measureCache.set('miniHero', width);
            HomepageCarouselManager.utils.lastMeasureTime = performance.now();
          });

          return width;
        }

        // ─── Peek controls (0..1 of the next card to show) ─────────────────────
        var PEEK_DESKTOP = parseFloat($slider.data('peek-desktop')); // optional
        var PEEK_MOBILE  = parseFloat($slider.data('peek-mobile'));  // optional
        function peekFrac(){
          var f = isDesktop()
            ? (isFinite(PEEK_DESKTOP) ? PEEK_DESKTOP : 0.50) // default 50% desktop
            : (isFinite(PEEK_MOBILE)  ? PEEK_MOBILE  : 0.15); // default 15% mobile
          return Math.max(0, Math.min(0.9, f));
        }

        // ─── Peek helpers ───────────────────────────────────────────────────────
        function estimateSlideW() {
          var railW = railWidth();
          var c = Math.max(1, cols());
          var totalGaps = Math.max(0, c - 1) * BASE_GAP;
          return Math.max(1, Math.round((railW - totalGaps) / c));
        }

        function measureSlideW() {
          var node =
            $slider.find('.slick-slide.slick-active').not('.slick-cloned')[0] ||
            $slider.find('.slick-slide').not('.slick-cloned')[0];
          if (!node) return estimateSlideW();
          const rect = node.getBoundingClientRect();
          return rect ? Math.max(1, Math.round(rect.width)) : estimateSlideW();
        }

        function computePeek(usingMeasured) {
          var sw = usingMeasured ? measureSlideW() : estimateSlideW();
          return Math.round(peekFrac() * (sw + BASE_GAP));
        }

        function setPeek(px) {
          var value = (px | 0) + 'px';

          // Batch style updates to prevent layout thrashing
          requestAnimationFrame(() => {
            // CSS var (first paint) + inline (beat theme css)
            $slider[0].style.setProperty('--peek', value);
            var list = $slider.find('.slick-list')[0];
            if (list) {
              list.style.paddingRight = value;
              list.style.paddingLeft = '0px';
            }
          });
        }

        function setPreInitCols() {
          $slider[0].style.setProperty('--cols', String(cols()));
        }

        // Reserve space **before** init to prevent first-paint jump
        setPreInitCols();
        setPeek(computePeek(false));

        // ─── Slick options ──────────────────────────────────────────────────────
        var opts = {
          slidesToShow: cols(),
          slidesToScroll: 1,
          centerMode: false,
          infinite: true,
          arrows: false,
          dots: false,
          rows: 0,
          adaptiveHeight: false,
          speed: 700,
          cssEase: 'cubic-bezier(.22,.61,.36,1)',
          swipeToSlide: true,
          touchThreshold: 10,
          edgeFriction: 0.06,
          waitForAnimate: false,
          autoplay: $slider.data('autoplay') === true,
          autoplaySpeed: parseInt($slider.data('speed'), 10) || 7000,
          // Mobile overrides (smoother + no multi-skip)
          responsive: [{
            breakpoint: DESKTOP_BP,         // < 1024 (your DESKTOP_BP)
            settings: {
              speed: 400,
              swipeToSlide: false,          // one slide per swipe
              waitForAnimate: true,         // no queuing
              touchThreshold: 25,
              edgeFriction: 0.12
            }
          }]
        };

        // ─── Post-layout refinement + mobile locking ────────────────────────────
        let layoutTimeout = null;
        function afterLayout() {
          if ($slider.data('peekLocked')) return; // on mobile we lock after init

          // Debounce layout updates to prevent excessive recalculations
          clearTimeout(layoutTimeout);
          layoutTimeout = setTimeout(() => {
            setPeek(computePeek(true));
          }, 16); // ~60fps max
        }

        $slider
          .on('init', function () {
            setPeek(computePeek(true));               // First real measure
            $slider.data('peekLocked', !isDesktop()); // Lock on mobile (iOS jiggle)
          })
          .on('setPosition', afterLayout)
          .on('reInit', afterLayout)
          .on('breakpoint', function () {
            $slider.slick('slickSetOption', 'slidesToShow', cols(), true);
            $slider.data('peekLocked', !isDesktop());
            setPreInitCols();
            setPeek(computePeek(true));
          });

        // ─── Initialize when visible (avoid zero-width measure) ────────────────
        function actuallyInit() {
          if ($slider.hasClass('slick-initialized')) return;

          // Enable hardware acceleration
          HomepageCarouselManager.utils.enableHardwareAcceleration($slider[0]);

          $slider.slick(opts);

          // Log initialization with performance metrics
          HomepageCarouselManager.logInit('Mini Hero', startTime);

          // Setup lazy loading for images
          HomepageCarouselManager.utils.setupLazyImageLoading($slider[0]);

          // Optimized image load handling
          const images = $slider.find('img');
          let loadedCount = 0;
          const totalImages = images.length;

          if (totalImages > 0) {
            images.each(function() {
              const img = this;
              if (img.complete) {
                loadedCount++;
                if (loadedCount === totalImages) afterLayout();
              } else {
                img.addEventListener('load', function() {
                  loadedCount++;
                  if (loadedCount === totalImages) afterLayout();
                }, { once: true });
              }
            });
          } else {
            afterLayout();
          }
        }

        if ('IntersectionObserver' in window) {
          var io = new IntersectionObserver(function (entries) {
            var entry = entries[0];
            if (entry && entry.isIntersecting) { io.disconnect(); actuallyInit(); }
          }, { rootMargin: '0px 0px 200px 0px', threshold: 0.01 });
          io.observe($slider[0]);
        } else {
          actuallyInit();
        }

        // ─── Width-only change handling (debounced / thresholded) ──────────────
        var lastW = railWidth();
        var lastCols = cols();

        function onWidthChanged() {
          var w = railWidth();
          var c = cols();
          var widthDelta = Math.abs(w - lastW);

          if (c !== lastCols || widthDelta >= 16) {
            lastW = w; lastCols = c;
            if ($slider.hasClass('slick-initialized')) {
              $slider.slick('slickSetOption', 'slidesToShow', c, true);
              if (!$slider.data('peekLocked')) setPeek(computePeek(true));
              else setPeek(computePeek(true)); // still refresh when breakpoint flips
            } else {
              setPreInitCols();
              setPeek(computePeek(false));
            }
          }
        }

        // Use homepage carousel manager for resize handling
        HomepageCarouselManager.addResizeHandler('miniHero', onWidthChanged);

        // Orientation change needs immediate handling
        function onOrientationChange() {
          $slider.data('peekLocked', !isDesktop());
          lastW = 0; lastCols = -1;
          onWidthChanged();
        }
        window.addEventListener('orientationchange', onOrientationChange);

        if ('ResizeObserver' in window) {
          try {
            var ro = new ResizeObserver(function () {
              var w = railWidth();
              if (w !== lastW) onWidthChanged();
            });
            ro.observe($slider[0]);
            $root.data('miniHeroRO', ro);
          } catch (e) {}
        }

        // Cleanup (theme editor)
        $root.data('miniHeroCleanup', function () {
          try { $slider.slick('unslick'); } catch (e) {}
          var ro = $root.data('miniHeroRO'); if (ro) ro.disconnect();
          HomepageCarouselManager.removeResizeHandler('miniHero');
          window.removeEventListener('orientationchange', onOrientationChange);
        });
      }

      function initAllVisible() {
        if (!window.jQuery || !jQuery.fn || !jQuery.fn.slick) return;
        jQuery('.mini-hero').each(function () { initMiniHero(this); });
      }

      document.addEventListener('DOMContentLoaded', initAllVisible);

      document.addEventListener('shopify:section:load', function (e) {
        var el = e.target.querySelector('.mini-hero') || e.target;
        if (el && el.classList && el.classList.contains('mini-hero')) initMiniHero(el);
      });
      document.addEventListener('shopify:section:unload', function (e) {
        jQuery(e.target).find('.mini-hero').each(function () {
          var cleanup = jQuery(this).data('miniHeroCleanup');
          if (cleanup) cleanup();
        });
      });

      document.addEventListener('tab:shown', function (e) {
        var $s = jQuery(e.target).find('.js-mini-hero-slider.slick-initialized');
        if ($s.length) $s.slick('setPosition');
      });
    })();

    /* ===== OPTIMIZED RSVLTS CLASSIC CAROUSEL ===== */
    (function ($) {
      'use strict';

      var UID = 0;

      function initLandscape($root) {
        const startTime = performance.now();
        var $slider = $root.find('.js-landscape-cards');
        if (!$slider.length || $slider.hasClass('slick-initialized')) return;

        var ns = '.landscape' + (++UID);

        // Settings
        var BASE_GAP     = parseInt($slider.data('gap'), 10) || 20;
        var DESKTOP_BP   = parseInt($slider.data('desktop-breakpoint'), 10) || 768;
        var SHOW_DESKTOP = parseInt($slider.data('show-desktop'), 10) || 3; // Use integer for slidesToShow

        function isDesktop(){ return window.matchMedia('(min-width:' + DESKTOP_BP + 'px)').matches; }

        // Optimized railWidth with batching and caching
        function railWidth() {
          const now = performance.now();
          if (now - HomepageCarouselManager.utils.lastMeasureTime < HomepageCarouselManager.utils.measureThrottle) {
            return HomepageCarouselManager.utils.measureCache.get('landscape') || 0;
          }

          const width = Math.round($slider[0].getBoundingClientRect().width || 0);
          HomepageCarouselManager.utils.measureCache.set('landscape', width);
          HomepageCarouselManager.utils.lastMeasureTime = now;
          return width;
        }

        // Desktop peek (fraction of next card visible)
        var PEEK_FRAC = 0.5;

        // --- helpers -----------------------------------------------------------
        function setColsVar(){ $slider[0].style.setProperty('--cols', String(SHOW_DESKTOP)); }

        function estimateSlideW() {
          var rw = railWidth();
          var c  = Math.max(1, SHOW_DESKTOP);
          var totalGaps = Math.max(0, c - 1) * BASE_GAP;
          return Math.max(1, Math.round((rw - totalGaps) / c));
        }
        function measureSlideW() {
          var node =
            $slider.find('.slick-slide.slick-active').not('.slick-cloned')[0] ||
            $slider.find('.slick-slide').not('.slick-cloned')[0];
          if (!node) return estimateSlideW();
          const rect = node.getBoundingClientRect();
          return rect ? Math.max(1, Math.round(rect.width)) : estimateSlideW();
        }
        function computePeek(usingMeasured){
          var sw = usingMeasured ? measureSlideW() : estimateSlideW();
          return Math.round(PEEK_FRAC * (sw + BASE_GAP));
        }
        function setPeek(px){
          var value = (px|0) + 'px';
          $slider[0].style.setProperty('--peek', value); // CSS var (used by pre-init & slick-list)
          var list = $slider.find('.slick-list')[0];
          if (list) { list.style.paddingRight = value; list.style.paddingLeft = '0px'; } // beat theme CSS
        }

        // Reserve space BEFORE init (desktop) to stop first-paint flash
        function reserveBeforeInit(){
          if (isDesktop()) {
            setColsVar();
            var pre = computePeek(false);                  // estimate using pre-init math
            $slider.data('prePeek', pre);
            setPeek(pre);                                  // SAME value we'll apply on init
          } else {
            $slider.removeData('prePeek');
            setPeek(0);
          }
        }


        // --- init / teardown ---------------------------------------------------
        function initSlick(){
          if ($slider.hasClass('slick-initialized')) return;

          // Defer initialization to prevent blocking
          requestAnimationFrame(() => {
            $slider
              .on('init' + ns, function(){
                // lock to the pre-init value to avoid a 2nd micro-nudge
                var pre = $slider.data('prePeek');
                setPeek(typeof pre === 'number' ? pre : computePeek(false));
                $slider.data('peekLocked', true);

                // Enable hardware acceleration
                HomepageCarouselManager.utils.enableHardwareAcceleration($slider[0]);

                // Log initialization with performance metrics
                HomepageCarouselManager.logInit('RSVLTS Classic', startTime);
              })
            .on('setPosition' + ns, function(){
              if (!$slider.data('peekLocked')) setPeek(computePeek(true));
            })
            .on('reInit' + ns, function(){
              if (!$slider.data('peekLocked')) setPeek(computePeek(true));
            });

            $slider.slick({
              slidesToShow: SHOW_DESKTOP,
              slidesToScroll: 1,
              arrows: false,
              dots: false,
              infinite: true,
              rows: 0,
              adaptiveHeight: false,
              speed: 700,
              cssEase: 'cubic-bezier(.22,.61,.36,1)',
              swipeToSlide: true,
              touchThreshold: 10,
              edgeFriction: 0.06,
              waitForAnimate: false,
              autoplay: $slider.data('autoplay') === true,
              autoplaySpeed: parseInt($slider.data('speed'), 10) || 7000
            });

            // If first image wasn't eager, keep the locked value (no change)
            var img = $slider.find('img')[0];
            if (img && !img.complete) img.addEventListener('load', function(){ /* no-op; locked */ }, { once:true });
          });
        }

        function unslick(){
          if ($slider.hasClass('slick-initialized')) {
            try { $slider.slick('unslick'); } catch(e){}
          }
          var list = $slider.find('.slick-list')[0];
          if (list) { list.style.paddingRight = ''; list.style.paddingLeft = ''; }
          $slider[0].style.removeProperty('--peek');
          $slider.removeData('peekLocked').removeData('prePeek');
        }

        function ensureMode(){
          if (isDesktop()) {
            reserveBeforeInit();
            // init when visible (avoid zero-width measurement)
            if ('IntersectionObserver' in window && !$slider.hasClass('slick-initialized')) {
              var io = new IntersectionObserver(function (entries) {
                var e = entries[0];
                if (e && e.isIntersecting) { io.disconnect(); initSlick(); }
              }, { rootMargin: '0px 0px 200px 0px', threshold: 0.01 });
              io.observe($slider[0]);
            } else {
              initSlick();
            }
          } else {
            unslick();
          }
        }

        // Initial
        ensureMode();

        // Width-only change handling (ignore tiny jiggles)
        var lastW = railWidth();
        var rt;
        function onResize(){
          clearTimeout(rt);
          rt = setTimeout(function(){
            var w = railWidth();
            if (Math.abs(w - lastW) >= 16) {
              lastW = w;
              // unlock for a real recalc
              $slider.removeData('peekLocked');
              ensureMode();
              if ($slider.hasClass('slick-initialized')) {
                var pre = computePeek(true);
                $slider.data('prePeek', pre);
                setPeek(pre);
                $slider.data('peekLocked', true); // re-lock until next real change
              }
            }
          }, 100);
        }

        // Use homepage carousel manager for resize handling
        HomepageCarouselManager.addResizeHandler('landscape', onResize);

        // Orientation change needs immediate handling
        function onOrientationChange() {
          onResize();
        }
        window.addEventListener('orientationchange', onOrientationChange);

        if ('ResizeObserver' in window) {
          try {
            var ro = new ResizeObserver(function(){
              var w = railWidth();
              if (w !== lastW) { lastW = w; onResize(); }
            });
            ro.observe($slider[0]);
            $root.data('landscapeRO', ro);
          } catch(e){}
        }

        // Cleanup
        $root.data('landscapeCleanup', function(){
          HomepageCarouselManager.removeResizeHandler('landscape');
          window.removeEventListener('orientationchange', onOrientationChange);
          var ro = $root.data('landscapeRO'); if (ro) ro.disconnect();
          unslick();
        });
      }

      function initAll(){ if (!$.fn.slick) return; $('.landscape-cards').each(function(){ initLandscape($(this)); }); }
      $(function(){ initAll(); });

      // Shopify editor hooks
      $(document).on('shopify:section:load', function(e){
        var $sec = $(e.target).find('.landscape-cards');
        if ($sec.length) initLandscape($sec);
      });
      $(document).on('shopify:section:unload', function(e){
        $(e.target).find('.landscape-cards').each(function(){
          var fn = $(this).data('landscapeCleanup'); if (fn) fn();
        });
      });
    })(jQuery);

    /* ===== OPTIMIZED TOP-10 CAROUSEL - MOBILE FIXED ===== */
    (function ($) {
      'use strict';

      var UID = 0;

      function initTop10($root) {
        const startTime = performance.now();
        var $slider = $root.find('.js-top10-slider');
        if (!$slider.length || $slider.hasClass('slick-initialized')) return;

        var ns = '.top10' + (++UID);

        // ─── Settings ───────────────────────────────────────────────────────────
        var BASE_GAP     = parseInt($slider.data('gap'), 10) || 20;
        var DESKTOP_BP   = parseInt($slider.data('desktop-breakpoint'), 10) || 768;
        var SHOW_DESKTOP = parseInt($slider.data('show-desktop'), 10) || 4;
        var SHOW_MOBILE  = parseInt($slider.data('show-mobile'), 10) || 1;

        function isDesktop () { return window.matchMedia('(min-width:' + DESKTOP_BP + 'px)').matches; }
        function cols()       { return isDesktop() ? SHOW_DESKTOP : SHOW_MOBILE; }

        // Mobile-optimized railWidth with aggressive caching
        function railWidth()  {
          const now = performance.now();
          const cacheKey = 'top10_' + (isDesktop() ? 'desktop' : 'mobile');

          // Balanced caching - not too aggressive to maintain functionality
          const cacheTime = isDesktop() ? 100 : 200; // Moderate cache on mobile
          if (now - HomepageCarouselManager.utils.lastMeasureTime < cacheTime) {
            return HomepageCarouselManager.utils.measureCache.get(cacheKey) || 0;
          }

          const element = $slider[0];
          if (!element) return 0;

          let width = HomepageCarouselManager.utils.measureCache.get(cacheKey) || 0;

          // Allow measurements but with throttling on mobile
          if (width === 0 || !$slider.hasClass('slick-initialized')) {
            HomepageCarouselManager.utils.queueMeasurement(() => {
              const rect = element.getBoundingClientRect();
              width = rect ? Math.round(rect.width || 0) : 0;
              HomepageCarouselManager.utils.measureCache.set(cacheKey, width);
              HomepageCarouselManager.utils.lastMeasureTime = performance.now();
            });
          }

          return width;
        }

        // ─── Peek controls (simplified for mobile) ──────────────────────────────
        var PEEK_DESKTOP = parseFloat($slider.data('peek-desktop')); // optional
        var PEEK_MOBILE  = parseFloat($slider.data('peek-mobile'));  // optional
        function peekFrac(){
          var f = isDesktop()
            ? (isFinite(PEEK_DESKTOP) ? PEEK_DESKTOP : 0.50) // 50% desktop
            : (isFinite(PEEK_MOBILE)  ? PEEK_MOBILE  : 0.15); // 15% mobile
          return Math.max(0, Math.min(0.9, f));
        }

        // ─── Simplified peek helpers for mobile performance ──────────────────────
        function estimateSlideW() {
          var railW = railWidth();
          var c = Math.max(1, cols());
          var totalGaps = Math.max(0, c - 1) * BASE_GAP;
          return Math.max(1, Math.round((railW - totalGaps) / c));
        }

        function measureSlideW() {
          // Skip expensive measurements on mobile
          if (!isDesktop()) {
            return estimateSlideW();
          }

          var node =
            $slider.find('.slick-slide.slick-active').not('.slick-cloned')[0] ||
            $slider.find('.slick-slide').not('.slick-cloned')[0];
          if (!node) return estimateSlideW();
          const rect = node.getBoundingClientRect();
          return rect ? Math.max(1, Math.round(rect.width)) : estimateSlideW();
        }

        function computePeek(usingMeasured) {
          var sw = usingMeasured ? measureSlideW() : estimateSlideW();
          return Math.round(peekFrac() * (sw + BASE_GAP));
        }

        function setPeek(px) {
          var value = (px | 0) + 'px';

          // Batch style updates to prevent layout thrashing
          requestAnimationFrame(() => {
            $slider[0].style.setProperty('--peek', value);
            var list = $slider.find('.slick-list')[0];
            if (list) {
              list.style.paddingRight = value;
              list.style.paddingLeft = '0px';
            }
          });
        }

        function setPreInitCols() {
          $slider[0].style.setProperty('--cols', String(cols()));
        }

        // Reserve space **before** init to prevent first-paint shift
        setPreInitCols();
        setPeek(computePeek(false));

        // Force immediate peek calculation on mobile for page load
        if (!isDesktop()) {
          setTimeout(() => {
            setPeek(computePeek(true));
          }, 100);
        }

        // ─── Mobile-optimized Slick options ──────────────────────────────────────
        var opts = {
          slidesToShow: cols(),
          slidesToScroll: 1,
          centerMode: false,
          infinite: true,
          arrows: false,
          dots: false,
          rows: 0,
          adaptiveHeight: false,
          speed: isDesktop() ? 700 : 500, // Faster on mobile
          cssEase: 'cubic-bezier(.22,.61,.36,1)',
          swipeToSlide: true,
          touchThreshold: isDesktop() ? 10 : 30, // Higher threshold on mobile
          edgeFriction: isDesktop() ? 0.06 : 0.15, // More friction on mobile
          waitForAnimate: !isDesktop(), // Wait on mobile to prevent conflicts
          autoplay: $slider.data('autoplay') === true,
          autoplaySpeed: parseInt($slider.data('speed'), 10) || 7000,
          // Mobile-specific overrides
          responsive: [{
            breakpoint: DESKTOP_BP,
            settings: {
              speed: 400,
              swipeToSlide: false,   // one slide per swipe
              waitForAnimate: true,  // no queuing
              touchThreshold: 50,    // Higher threshold for better mobile experience
              edgeFriction: 0.20,    // More friction to prevent accidental swipes
              // Disable autoplay on mobile to prevent conflicts
              autoplay: false
            }
          }]
        };

        // ─── Mobile-optimized layout handling ────────────────────────────────────
        var lastPeekValue = null;
        var layoutTimeout = null;
        var isLayoutLocked = false;

        function afterLayout() {
          // Skip layout updates on mobile to prevent freezing
          if (!isDesktop()) {
            return;
          }

          // Allow layout updates on mobile for peek functionality
          if ($slider.data('peekLocked')) return;

          // More aggressive debouncing on mobile
          clearTimeout(layoutTimeout);
          layoutTimeout = setTimeout(function() {
            var newPeek = computePeek(true);
            // Only update if the peek value actually changed to prevent flickering
            if (newPeek !== lastPeekValue) {
              lastPeekValue = newPeek;
              setPeek(newPeek);
            }
          }, isDesktop() ? 50 : 200); // Increased delay on mobile to prevent freezing
        }

        $slider
          .on('init' + ns, function () {
            setPeek(computePeek(true));               // first real measure
            $slider.data('peekLocked', false); // Don't lock peek on mobile anymore
            isLayoutLocked = false; // Allow layout updates for peek functionality
            HomepageCarouselManager.logInit('Top-10', startTime);
          })
          .on('setPosition' + ns, function() {
            // Allow layout updates on mobile for peek functionality
            afterLayout();
          })
          .on('reInit' + ns, function() {
            // Allow reInit on both desktop and mobile
            afterLayout();
          })
          .on('breakpoint' + ns, function () {
            $slider.slick('slickSetOption', 'slidesToShow', cols(), true);
            $slider.data('peekLocked', false); // Don't lock peek
            isLayoutLocked = false; // Allow layout updates
            setPreInitCols();
            setPeek(computePeek(true));
          });

        // ─── Initialize when visible (avoid zero-width measure) ────────────────
        function actuallyInit() {
          if ($slider.hasClass('slick-initialized')) return;

          // Defer initialization to next frame to prevent blocking
          requestAnimationFrame(() => {
            const frameStart = performance.now();

            // Enable hardware acceleration
            HomepageCarouselManager.utils.enableHardwareAcceleration($slider[0]);

            // Check if we're taking too long before Slick init
            if (performance.now() - frameStart > 5) {
              // Defer Slick initialization to next frame
              requestAnimationFrame(() => {
                $slider.slick(opts);

                // Defer heavy operations to another frame
                requestAnimationFrame(() => {
                  HomepageCarouselManager.utils.stabilizeProductElements($slider[0]);
                  HomepageCarouselManager.logInit('Top-10', startTime);
                });
              });
            } else {
              $slider.slick(opts);

              // Defer heavy operations to next frame
              requestAnimationFrame(() => {
                HomepageCarouselManager.utils.stabilizeProductElements($slider[0]);
                HomepageCarouselManager.logInit('Top-10', startTime);
              });
            }

            // Force peek calculation immediately after Slick initialization on mobile
            if (!isDesktop()) {
              setTimeout(() => {
                setPeek(computePeek(true));
                // Trigger a refresh to ensure proper display
                $slider.slick('setPosition');
              }, 50);
            }

            // Skip image load handling on mobile to prevent conflicts
            if (isDesktop()) {
              var img = $slider.find('img')[0];
              if (img && !img.complete) img.addEventListener('load', function(){ afterLayout(); }, { once:true });
            }
          });
        }

        if ('IntersectionObserver' in window) {
          var io = new IntersectionObserver(function (entries) {
            var entry = entries[0];
            if (entry && entry.isIntersecting) {
              io.disconnect();
              // Shorter delay on mobile for faster initialization
              setTimeout(actuallyInit, isDesktop() ? 50 : 50);
            }
          }, { rootMargin: '0px 0px 200px 0px', threshold: 0.01 });
          io.observe($slider[0]);
        } else {
          // Defer even without IntersectionObserver
          setTimeout(actuallyInit, isDesktop() ? 100 : 100);
        }

        // Force initialization on mobile after a short delay to ensure proper layout
        if (!isDesktop()) {
          setTimeout(() => {
            if (!$slider.hasClass('slick-initialized')) {
              actuallyInit();
            }
          }, 200);
        }

        // ─── Simplified resize handling (mobile-optimized) ───────────────────────
        var lastW = 0;
        var lastCols = 0;
        var resizeTimeout = null;

        function onWidthChanged() {
          // Skip resize handling on mobile to prevent freezing
          if (!isDesktop()) {
            return;
          }

          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(function() {
            var w = railWidth();
            var c = cols();
            if (c !== lastCols || Math.abs(w - lastW) >= 16) {
              lastW = w; lastCols = c;
              if ($slider.hasClass('slick-initialized')) {
                $slider.slick('slickSetOption', 'slidesToShow', c, true);
                setPeek(computePeek(true));
              } else {
                setPreInitCols();
                setPeek(computePeek(false));
              }
            }
          }, isDesktop() ? 200 : 500); // Much longer delay on mobile
        }

        // Use homepage carousel manager for resize handling
        HomepageCarouselManager.addResizeHandler('top10', onWidthChanged);

        // Orientation change needs immediate handling
        function onOrientationChange() {
          $slider.data('peekLocked', false); // Don't lock peek
          isLayoutLocked = false; // Allow layout updates
          lastW = 0; lastCols = -1;
          // Handle orientation change on both desktop and mobile
          setTimeout(function() {
            if ($slider.hasClass('slick-initialized')) {
              $slider.slick('slickSetOption', 'slidesToShow', cols(), true);
              setPeek(computePeek(true));
            }
          }, isDesktop() ? 100 : 300); // Shorter delay on desktop, longer on mobile
        }
        window.addEventListener('orientationchange', onOrientationChange);

        // Only use ResizeObserver on desktop
        if ('ResizeObserver' in window && isDesktop()) {
          try {
            var ro = new ResizeObserver(function () {
              var w = railWidth();
              if (w !== lastW) onWidthChanged();
            });
            ro.observe($slider[0]);
            $root.data('top10RO', ro);
          } catch (e) {}
        }

        // Cleanup
        $root.data('top10Cleanup', function(){
          HomepageCarouselManager.removeResizeHandler('top10');
          window.removeEventListener('orientationchange', onOrientationChange);
          var ro = $root.data('top10RO'); if (ro) ro.disconnect();
          clearTimeout(layoutTimeout);
          clearTimeout(resizeTimeout);
          if ($slider.hasClass('slick-initialized')) { try { $slider.slick('unslick'); } catch(e){} }
        });
      }

      function initAll(){ if (!$.fn.slick) return; $('.top10').each(function(){ initTop10($(this)); }); }
      $(function(){ initAll(); });

      // Theme editor hooks
      $(document).on('shopify:section:load', function(e){
        var $sec = $(e.target).find('.top10');
        if ($sec.length) initTop10($sec);
      });
      $(document).on('shopify:section:unload', function(e){
        $(e.target).find('.top10').each(function(){
          var fn = $(this).data('top10Cleanup'); if (fn) fn();
        });
      });
    })(jQuery);

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initHomepageCarousels);
    } else {
      initHomepageCarousels();
    }

    // Also initialize on window load to ensure proper mobile peek
    window.addEventListener('load', () => {
      // Re-trigger carousel initialization for mobile peek
      if (window.innerWidth <= 768) {
        document.querySelectorAll('.js-top10-slider').forEach(slider => {
          if (slider.classList.contains('slick-initialized')) {
            // Force peek calculation on mobile after full page load
            const $slider = jQuery(slider);
            const isDesktop = () => window.matchMedia('(min-width: 768px)').matches;
            if (!isDesktop()) {
              setTimeout(() => {
                // Trigger a refresh to ensure proper display
                $slider.slick('setPosition');
              }, 100);
            }
          }
        });
      }
    });

    // Export for use in other modules
    window.HomepageCarousels = {
      HomepageCarouselManager
    };
  })();
